"use client"

import { useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/common"
import { isDateBlocked, isSameDay, getDatesBetween, getAllowedDateRange } from "@lib/util/rental"
import { useTranslations } from "next-intl"

type DateStatus = 'available' | 'booked' | 'partial'

interface RentalCalendarProps {
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  bookedDates?: Date[]
  partialDates?: Date[]
  disabled?: boolean
  hoverPreview?: string | null
}

export default function RentalCalendar({
  selectedDate,
  onDateSelect,
  bookedDates = [],
  partialDates = [],
  disabled = false,
  hoverPreview,
}: RentalCalendarProps) {
  const t = useTranslations('product.rental')
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)

  // Get the 7-day date range (today to today+7)
  const { minDate, maxDate } = getAllowedDateRange()
  const dates = useMemo(() => getDatesBetween(minDate, maxDate), [minDate, maxDate])

  // Check if a date is booked
  const isBooked = (date: Date): boolean => {
    return bookedDates.some(bookedDate => isSameDay(bookedDate, date))
  }

  // Check if a date is partially available
  const isPartial = (date: Date): boolean => {
    return partialDates.some(partialDate => isSameDay(partialDate, date))
  }

  // Get date status
  const getDateStatus = (date: Date): DateStatus => {
    if (isBooked(date)) return 'booked'
    if (isPartial(date)) return 'partial'
    return 'available'
  }

  // Check if a date is selected
  const isSelected = (date: Date): boolean => {
    return selectedDate !== null && isSameDay(selectedDate, date)
  }

  // Check if date is today
  const isToday = (date: Date): boolean => {
    return isSameDay(date, new Date())
  }

  // Handle date click
  const handleDateClick = (date: Date) => {
    if (disabled || isBooked(date) || isDateBlocked(date)) return
    onDateSelect(date)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let targetIndex = index
    switch (e.key) {
      case 'ArrowRight':
        targetIndex = Math.min(index + 1, dates.length - 1)
        e.preventDefault()
        break
      case 'ArrowLeft':
        targetIndex = Math.max(index - 1, 0)
        e.preventDefault()
        break
      default:
        return
    }
    const buttons = (e.currentTarget as HTMLElement)
      .parentElement?.querySelectorAll<HTMLButtonElement>('button[data-calendar-date]')
    buttons?.[targetIndex]?.focus()
  }

  // Get day name
  const getDayName = (date: Date, locale: string = 'ru'): string => {
    const localeMap: Record<string, string> = {
      ru: 'ru-RU',
      en: 'en-US',
      cs: 'cs-CZ',
      uk: 'uk-UA',
    }
    return date.toLocaleDateString(localeMap[locale] || 'ru-RU', { weekday: 'short' })
  }

  // Get day number
  const getDayNumber = (date: Date): string => {
    return date.getDate().toString()
  }

  // Get month name
  const getMonthName = (date: Date, locale: string = 'ru'): string => {
    const localeMap: Record<string, string> = {
      ru: 'ru-RU',
      en: 'en-US',
      cs: 'cs-CZ',
      uk: 'uk-UA',
    }
    return date.toLocaleDateString(localeMap[locale] || 'ru-RU', { month: 'short' })
  }

  return (
    <div className="w-full">
      <div className="text-sm font-medium text-foreground-secondary mb-2">
        {t('selectStartDate')}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-8 gap-1" role="listbox" aria-label={t('selectStartDate')}>
        {dates.map((date, index) => {
          const blocked = isDateBlocked(date)
          const booked = isBooked(date)
          const partial = isPartial(date)
          const selected = isSelected(date)
          const today = isToday(date)
          const status = getDateStatus(date)
          const clickable = !disabled && !blocked && !booked

          return (
            <Button
              variant="ghost"
              key={index}
              type="button"
              role="option"
              aria-selected={selected}
              aria-disabled={!clickable}
              data-calendar-date={index}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => clickable && setHoveredDate(date)}
              onMouseLeave={() => setHoveredDate(null)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              disabled={!clickable}
              className={cn(
                "h-auto p-0 rounded-none font-normal",
                "relative flex flex-col items-center justify-center p-2 rounded-lg transition-all",
                "min-h-[64px] text-center",
                // Base styles
                clickable && "hover:bg-accent cursor-pointer",
                // Selected
                selected && "bg-primary text-primary-foreground hover:bg-primary-hover",
                // Today (not selected) - ring highlight only
                today && !selected && "ring-2 ring-primary ring-inset",
                // Booked - red
                booked && "bg-red-50 dark:bg-red-950 text-red-400 line-through opacity-60 cursor-not-allowed",
                // Partially available - yellow
                partial && !selected && !blocked && "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400",
                // Available (not partial, not booked, not selected, not blocked)
                status === 'available' && !blocked && !selected && "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400",
                // Blocked (before today or after 7 days)
                blocked && "bg-muted text-muted-foreground opacity-40 cursor-not-allowed",
                // Disabled state
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <span className={cn(
                "text-xs uppercase font-medium",
                selected ? "text-primary-foreground" : "text-foreground-secondary"
              )}>
                {getDayName(date)}
              </span>
              <span className={cn(
                "text-lg font-bold",
                selected ? "text-primary-foreground" : ""
              )}>
                {getDayNumber(date)}
              </span>
              <span className={cn(
                "text-xs",
                selected ? "text-primary-foreground/80" : "text-foreground-tertiary"
              )}>
                {getMonthName(date)}
              </span>

              {/* Hover tooltip with price preview */}
              {hoveredDate && isSameDay(hoveredDate, date) && hoverPreview && clickable && !selected && (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-10 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-md border border-border whitespace-nowrap pointer-events-none">
                  {hoverPreview}
                </div>
              )}
            </Button>
          )
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-foreground-secondary">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800" />
          <span>{t('calendar.available')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800" />
          <span>{t('calendar.partiallyAvailable')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800" />
          <span>{t('calendar.booked')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-primary" />
          <span>{t('selected')}</span>
        </div>
      </div>
    </div>
  )
}
